import { authService } from '@bri/shared-components';
import { API_ENDPOINTS } from '@bri/shared-components';
import { getErrorMessage } from '@/lib/utils';

export interface Workspace {
  workspaceId: string;
  name: string;
  description?: string;
  ownerUserId: string;
  createdAt: string;
  userRole?: 'owner' | 'editor' | 'viewer';
  joinedAt?: string;
  memberCount?: number;
  projectCount?: number;
}

export interface CreateWorkspaceRequest {
  name: string;
  description?: string;
}

export interface TeamMember {
  userId: string;
  email: string;
  name?: string;
  role: 'owner' | 'editor' | 'viewer';
  joinedAt: string;
  status: 'active';
}

export interface PendingInvitation {
  invitationId: string;
  email: string;
  role: 'owner' | 'editor' | 'viewer';
  invitedAt: string;
  expiresAt: string;
  status: 'pending';
}

export interface WorkspaceListResponse {
  success: boolean;
  data: {
    workspaces: Workspace[];
    count: number;
  };
  error?: string;
}

export interface WorkspaceResponse {
  success: boolean;
  data: Workspace;
  error?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface WorkspaceApiRecord {
  workspaceId: string;
  name: string;
  description?: string;
  ownerUserId: string;
  createdAt: string;
  userRole?: 'owner' | 'editor' | 'viewer';
  memberCount?: number;
  projectCount?: number;
}

interface WorkspaceCollectionPayload {
  workspaces: WorkspaceApiRecord[];
}

interface WorkspaceDetailPayload extends WorkspaceApiRecord {
  workspace?: WorkspaceApiRecord;
}

interface WorkspaceMembersPayload {
  members: TeamMember[];
  pendingInvitations: PendingInvitation[];
  totalMembers: number;
  totalPending: number;
}

interface InviteMemberRequest {
  email: string;
  role: 'owner' | 'editor' | 'viewer';
  personalMessage?: string;
}

function toWorkspace(record: WorkspaceApiRecord): Workspace {
  return {
    workspaceId: record.workspaceId,
    name: record.name,
    description: record.description,
    ownerUserId: record.ownerUserId,
    createdAt: record.createdAt,
    userRole: record.userRole || 'viewer',
    memberCount: record.memberCount || 1,
    projectCount: record.projectCount || 0,
  };
}

class WorkspaceService {
  /**
   * List all workspaces for the current user
   */
  async listWorkspaces(): Promise<Workspace[]> {
    try {
      const response = await authService.apiRequest(API_ENDPOINTS.WORKSPACES, {
        method: 'GET',
      }) as ApiResponse<WorkspaceCollectionPayload>;

      if (response.success && response.data?.workspaces) {
        return response.data.workspaces.map(toWorkspace);
      }

      throw new Error(response.error || 'Failed to fetch workspaces');
    } catch (error: unknown) {
      console.error('WorkspaceService: Error fetching workspaces:', error);
      const errorMessage = getErrorMessage(error);
      
      // Return empty array instead of throwing to allow graceful fallback
      if (errorMessage.includes('API request failed') || 
          errorMessage.includes('fetch') || 
          errorMessage.includes('No authentication token') ||
          errorMessage.includes('Failed to fetch')) {
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Get a specific workspace by ID
   */
  async getWorkspace(workspaceId: string): Promise<Workspace> {
    try {
      const endpoint = API_ENDPOINTS.WORKSPACE_DETAIL.replace(':id', workspaceId);
      const response = await authService.apiRequest(endpoint, {
        method: 'GET',
      }) as ApiResponse<WorkspaceApiRecord>;

      if (response.success && response.data) {
        return toWorkspace(response.data);
      }

      throw new Error(response.error || 'Failed to fetch workspace details');
    } catch (error: unknown) {
      console.error('WorkspaceService: Error fetching workspace details:', error);
      throw error;
    }
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(workspaceData: CreateWorkspaceRequest): Promise<Workspace> {
    try {
      const response = await authService.apiRequest(API_ENDPOINTS.WORKSPACES, {
        method: 'POST',
        body: JSON.stringify(workspaceData),
      }) as ApiResponse<WorkspaceDetailPayload>;

      if (response.success && response.data) {
        // The backend returns the workspace nested in response.data.workspace
        const workspaceRecord = response.data.workspace || response.data;

        return {
          ...toWorkspace(workspaceRecord),
          userRole: 'owner',
          memberCount: 1,
          projectCount: 0,
        };
      }

      throw new Error(response.error || 'Failed to create workspace');
    } catch (error: unknown) {
      console.error('WorkspaceService: Error creating workspace:', error);
      throw error;
    }
  }

  /**
   * Update workspace settings
   */
  async updateWorkspace(workspaceId: string, updates: { name?: string; description?: string }): Promise<Workspace> {
    try {
      const endpoint = `/workspaces/${workspaceId}`;
      const response = await authService.apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(updates),
      }) as ApiResponse<WorkspaceDetailPayload>;

      if (response.success && response.data) {
        const workspaceRecord = response.data.workspace || response.data;

        return {
          ...toWorkspace(workspaceRecord),
          userRole: 'owner',
        };
      }

      throw new Error(response.error || 'Failed to update workspace');
    } catch (error: unknown) {
      console.error('WorkspaceService: Error updating workspace:', error);
      throw error;
    }
  }

  /**
   * Invite a member to a workspace
   */
  async inviteMember(workspaceId: string, email: string, role: 'owner' | 'editor' | 'viewer' = 'viewer', personalMessage?: string): Promise<void> {
    try {
      const endpoint = API_ENDPOINTS.INVITE_MEMBER.replace(':id', workspaceId);
      const requestBody: InviteMemberRequest = { email, role };
      if (personalMessage) {
        requestBody.personalMessage = personalMessage;
      }
      
      const response = await authService.apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }) as ApiResponse<Record<string, never>>;

      if (!response.success) {
        throw new Error(response.error || 'Failed to invite member');
      }
    } catch (error: unknown) {
      console.error('WorkspaceService: Error inviting member:', error);
      throw error;
    }
  }

  /**
   * Get workspace members and pending invitations
   */
  async getWorkspaceMembers(workspaceId: string): Promise<{
    members: TeamMember[];
    pendingInvitations: PendingInvitation[];
    totalMembers: number;
    totalPending: number;
  }> {
    try {
      const endpoint = `/workspaces/${workspaceId}/members`;
      const response = await authService.apiRequest(endpoint, {
        method: 'GET',
      }) as ApiResponse<WorkspaceMembersPayload>;

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.error || 'Failed to get workspace members');
    } catch (error: unknown) {
      console.error('WorkspaceService: Error getting workspace members:', error);
      throw error;
    }
  }

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(workspaceId: string, invitationId: string): Promise<void> {
    try {
      const endpoint = `/workspaces/${workspaceId}/invitations/${invitationId}`;
      const response = await authService.apiRequest(endpoint, {
        method: 'DELETE',
      }) as ApiResponse<Record<string, never>>;

      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel invitation');
      }
    } catch (error: unknown) {
      console.error('WorkspaceService: Error cancelling invitation:', error);
      throw error;
    }
  }

    /**
   * Update a member's role in a workspace
   */
  async updateMemberRole(workspaceId: string, userId: string, role: 'owner' | 'editor' | 'viewer'): Promise<void> {
    try {
      const endpoint = API_ENDPOINTS.UPDATE_MEMBER
        .replace(':id', workspaceId)
        .replace(':userId', userId);
      
      const response = await authService.apiRequest(endpoint, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }) as ApiResponse<Record<string, never>>;

      if (!response.success) {
        throw new Error(response.error || 'Failed to update member role');
      }
    } catch (error: unknown) {
      console.error('WorkspaceService: Error updating member role:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a workspace
   */
  async removeMember(workspaceId: string, userId: string): Promise<void> {
    try {
      const endpoint = `/workspaces/${workspaceId}/members/${userId}`;
      const response = await authService.apiRequest(endpoint, {
        method: 'DELETE',
      }) as ApiResponse<Record<string, never>>;

      if (!response.success) {
        throw new Error(response.error || 'Failed to remove member');
      }
    } catch (error: unknown) {
      console.error('WorkspaceService: Error removing member:', error);
      throw error;
    }
  }

  /**
   * Auto-create a default workspace for users who don't have any
   */
  async createDefaultWorkspace(userEmail: string): Promise<Workspace> {
    try {
      // Generate a default workspace name based on user email
      const userName = userEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
      const defaultWorkspaceName = `${userName}'s Workspace`;
      
      const workspaceData: CreateWorkspaceRequest = {
        name: defaultWorkspaceName,
        description: 'Your default workspace',
      };

      const newWorkspace = await this.createWorkspace(workspaceData);
      return newWorkspace;
    } catch (error: unknown) {
      console.error('WorkspaceService: Error auto-creating default workspace:', error);
      throw error;
    }
  }
}

export const workspaceService = new WorkspaceService();
